import { useEffect, useMemo, useState } from 'react';
import type { Product } from '@bbpos/types';
import type { CurrencyCode } from '@bbpos/types';
import { Button, Input, Toggle } from '@bbpos/ui';
import { cn } from '@/constants/design-tokens';
import { formatPaise, marginPercent } from '@/utils/currency';
import { paiseToRupeeInput, rupeeInputToPaise } from '@/utils/price-input';
import { variantPriceChanges } from './variant-pricing-edits';
import type { VariantPriceEdit, VariantPriceUpdate } from './variant-pricing-edits';

/**
 * The parent's Variants tab table (POS comments item 3): a product with variants
 * is priced per variant, so each row's Price and Cost can be typed in place and
 * saved together. No "Extra" column — each variant stores its own price (OD5).
 */
export function VariantPricingTable({
  variants,
  currency,
  canEditPrices,
  saving,
  onSave,
  onToggleActive,
  togglingActive,
  onEdit,
  onDirtyChange,
}: {
  variants: Array<Product & { id: string }>;
  currency: CurrencyCode;
  /** Only an Owner may change prices (the API refuses anyone else). */
  canEditPrices: boolean;
  saving: boolean;
  onSave: (updates: VariantPriceUpdate[]) => Promise<void>;
  onToggleActive: (variantId: string, isActive: boolean) => void;
  togglingActive: boolean;
  onEdit: (variantId: string) => void;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const [edits, setEdits] = useState<Record<string, VariantPriceEdit>>({});
  const changes = useMemo(
    () => variantPriceChanges(variants, edits, currency),
    [variants, edits, currency],
  );
  const dirty = changes.updates.length > 0 || changes.errors.length > 0;

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  function edit(id: string, field: keyof VariantPriceEdit, value: string) {
    setEdits((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  }

  const shown = (v: Product & { id: string }, field: keyof VariantPriceEdit) =>
    edits[v.id]?.[field] ?? paiseToRupeeInput(Number(v[field] ?? 0), currency);

  async function save() {
    try {
      await onSave(changes.updates);
      setEdits({});
    } catch {
      // onSave reported which variant failed; rows already saved now match the
      // refetched values, so only the unsaved edits stay marked.
    }
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-neutral-200 dark:border-neutral-700">
              <th className="px-3 py-2 text-left font-medium text-neutral-500">Variant</th>
              <th className="px-3 py-2 text-left font-medium text-neutral-500">SKU</th>
              <th className="px-3 py-2 text-left font-medium text-neutral-500">Barcode</th>
              <th className="px-3 py-2 text-right font-medium text-neutral-500">Price</th>
              <th className="px-3 py-2 text-right font-medium text-neutral-500">Cost</th>
              <th className="px-3 py-2 text-right font-medium text-neutral-500">Margin</th>
              <th className="px-3 py-2 text-right font-medium text-neutral-500">Stock</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {variants.map((v) => {
              const price = rupeeInputToPaise(shown(v, 'price'), currency) ?? 0;
              const cost = rupeeInputToPaise(shown(v, 'cost'), currency) ?? 0;
              const rowError = changes.errors.find((e) => e.id === v.id)?.message;
              const changed = changes.updates.some((u) => u.id === v.id);
              return (
                <tr
                  key={v.id}
                  className={cn(
                    'border-b border-neutral-100 even:bg-neutral-50 hover:bg-brand-50 transition-colors duration-200 dark:border-neutral-800 dark:even:bg-neutral-800/40 dark:hover:bg-neutral-800',
                    !v.is_active && 'opacity-50',
                    changed && 'bg-violet-50/60 dark:bg-violet-900/10',
                  )}
                >
                  <td className="px-3 py-2 text-neutral-900 dark:text-neutral-100">
                    <div className="flex flex-wrap items-center gap-1">
                      {Object.entries(v.variant_attributes ?? {}).length > 0
                        ? Object.entries(v.variant_attributes ?? {}).map(([name, value]) => (
                            <span
                              key={name}
                              className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-700 dark:bg-violet-900/30 dark:text-violet-300"
                              title={name}
                            >
                              {String(value)}
                            </span>
                          ))
                        : v.name}
                      {!v.is_active && (
                        <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                          Inactive
                        </span>
                      )}
                    </div>
                    {rowError && <p className="mt-1 text-xs text-danger-600 dark:text-danger-400">{rowError}</p>}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-neutral-600 dark:text-neutral-300">{v.sku}</td>
                  <td className="px-3 py-2 font-mono text-xs text-neutral-500 dark:text-neutral-400">{v.barcode || '—'}</td>
                  {(['price', 'cost'] as const).map((field) => (
                    <td key={field} className="px-3 py-2 text-right tabular-nums">
                      {canEditPrices ? (
                        <div className="ml-auto w-28">
                          <Input
                            id={`variant-${v.id}-${field}`}
                            aria-label={`${field === 'price' ? 'Price' : 'Cost'} of ${v.name}`}
                            type="number"
                            value={shown(v, field)}
                            onChange={(val) => edit(v.id, field, val)}
                          />
                        </div>
                      ) : (
                        <span className={field === 'price' ? 'text-neutral-900 dark:text-neutral-100' : 'text-neutral-600 dark:text-neutral-300'}>
                          {formatPaise(Number(v[field] ?? 0), currency)}
                        </span>
                      )}
                    </td>
                  ))}
                  <td
                    className={cn(
                      'px-3 py-2 text-right tabular-nums text-xs font-medium',
                      price > cost ? 'text-success-600 dark:text-success-400' : 'text-danger-600 dark:text-danger-400',
                    )}
                  >
                    {price > 0 ? `${marginPercent(price, cost)}%` : '—'}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-neutral-600 dark:text-neutral-300">{v.stock_quantity ?? 0}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex items-center justify-end gap-3">
                      <Toggle
                        checked={v.is_active}
                        onChange={(next) => onToggleActive(v.id, next)}
                        label="Active"
                        size="sm"
                        disabled={togglingActive}
                        id={`variant-${v.id}-active`}
                      />
                      <Button variant="ghost" size="sm" onClick={() => onEdit(v.id)}>
                        Edit
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {canEditPrices && dirty && (
        <div className="mt-3 flex flex-wrap items-center justify-end gap-2 animate-fade-in">
          <span className="text-xs text-neutral-500 dark:text-neutral-400">
            {changes.updates.length} variant{changes.updates.length === 1 ? '' : 's'} changed
          </span>
          <Button variant="ghost" size="sm" onClick={() => setEdits({})} disabled={saving}>
            Discard
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => { void save(); }}
            disabled={saving || changes.errors.length > 0 || changes.updates.length === 0}
            data-testid="save-variant-prices"
          >
            {saving ? 'Saving…' : 'Save prices'}
          </Button>
        </div>
      )}
    </div>
  );
}
